import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Input,
  Button,
  Spinner,
  Modal,
  ModalContent,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Slider,
  Checkbox,
  RadioGroup,
  Radio,
  Select,
  SelectItem,
  Chip,
} from '@nextui-org/react';
import {
  Search,
  SlidersHorizontal,
  Trophy,
  MapPin,
  ChevronRight,
  Clock,
  X,
  Calendar,
  ArrowUpDown,
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import tournamentService, {
  Tournament,
} from '../services/tournamentService';
import subtournamentService from '../services/subtournamentService';

const ACCENT = '#3fced1';
const BATCH_SIZE = 20;

type DateFilter = 'all' | 'today' | 'week' | 'month' | 'custom';
type SortBy = 'quality' | 'date' | 'created';

const TournamentPage: React.FC = () => {
  // Data
  const [allTournaments, setAllTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination (lazy/batch loading within client-side filtered list)
  const [visibleCount, setVisibleCount] = useState(BATCH_SIZE);

  // Search
  const [searchTerm, setSearchTerm] = useState('');

  // Filters
  const [steeldart, setSteeldart] = useState(true);
  const [edart, setEdart] = useState(true);
  const [dateFilter, setDateFilter] = useState<DateFilter>('all');
  const [customDate, setCustomDate] = useState<string>('');
  const [feeRange, setFeeRange] = useState<[number, number]>([0, 100]);
  const [sortBy, setSortBy] = useState<SortBy>('date');
  const [sortAsc, setSortAsc] = useState(true);

  // Mobile filter modal
  const [filterOpen, setFilterOpen] = useState(false);

  // --------------------------------------------
  // Data Loading
  // --------------------------------------------
  const loadTournaments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Subtournaments parallel laden (für calcDates/calcCategories)
      const subsPromise = subtournamentService.getAllSubtournaments();

      const pageSize = 500;
      const maxPages = 10;
      const all: Tournament[] = [];
      let offset = 0;
      for (let i = 0; i < maxPages; i++) {
        const res = await tournamentService.getTournaments({ limit: pageSize, offset });
        const batch = res.data || [];
        if (batch.length === 0) break;
        all.push(...batch);
        if (batch.length < pageSize) break;
        offset += pageSize;
      }

      await subsPromise;
      setAllTournaments(all);
    } catch (err) {
      console.error('Fehler beim Laden der Turniere:', err);
      setError('Turniere konnten nicht geladen werden.');
      setAllTournaments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTournaments();
  }, [loadTournaments]);

  // --------------------------------------------
  // Filter & Sort (client-side, reaktiv)
  // --------------------------------------------
  const filtered = useMemo(() => {
    let list = allTournaments.slice();

    // Vergangene Turniere ausblenden (wie Flutter validDates):
    // Mindestens ein Termin (Tournament ODER Subtournaments) muss >= heute 00:00 liegen
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    list = list.filter((t) => {
      const dates = tournamentService.calcDates(t);
      if (dates.length === 0) return false;
      return dates.some((ts) => ts >= todayStart);
    });

    // Kategorie-Filter
    const activeCats: string[] = [];
    if (steeldart) activeCats.push('steeldart');
    if (edart) activeCats.push('edart');
    if (activeCats.length === 0) {
      // Keine Kategorie ausgewählt -> keine Ergebnisse
      return [];
    }
    // Wenn beide Kategorien aktiv, keinen Kategorie-Filter anwenden (zeige alles)
    const bothActive = steeldart && edart;
    if (!bothActive) {
      list = list.filter((t) => {
        const cats = tournamentService.calcCategories(t);
        if (cats.length === 0) {
          // Turniere ohne Kategorie-Info werden nicht ausgeschlossen
          return true;
        }
        const lower = cats.map((c) => c.toLowerCase());
        return activeCats.some((c) => lower.includes(c));
      });
    }

    // Zeitraum-Filter
    if (dateFilter !== 'all') {
      const start = todayStart;
      let end = start;
      if (dateFilter === 'today') {
        end = start + 24 * 60 * 60 * 1000 - 1;
      } else if (dateFilter === 'week') {
        end = start + 7 * 24 * 60 * 60 * 1000 - 1;
      } else if (dateFilter === 'month') {
        end = start + 30 * 24 * 60 * 60 * 1000 - 1;
      } else if (dateFilter === 'custom' && customDate) {
        const d = new Date(customDate);
        if (!isNaN(d.getTime())) {
          const s = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
          list = list.filter((t) => {
            const next = tournamentService.getNextTimestamp(t);
            if (next === null) return false;
            const day = new Date(next);
            return day.toDateString() === new Date(s).toDateString();
          });
          end = -1; // schon gefiltert
        }
      }
      if (end !== -1 && dateFilter !== 'custom') {
        list = list.filter((t) => {
          const next = tournamentService.getNextTimestamp(t);
          return next !== null && next >= start && next <= end;
        });
      }
    }

    // Startgeld-Filter (nur anwenden, wenn User den Range verändert hat)
    const feeFilterActive = feeRange[0] !== 0 || feeRange[1] !== 100;
    if (feeFilterActive) {
      list = list.filter((t) => {
        const fee = typeof t.entryFee === 'number' ? t.entryFee : 0;
        return fee >= feeRange[0] && fee <= feeRange[1];
      });
    }

    // Suche (Name oder Adresse)
    if (searchTerm.trim().length > 0) {
      const q = searchTerm.trim().toLowerCase();
      list = list.filter((t) => {
        const name = (t.name || '').toLowerCase();
        const addr = tournamentService.formatAddress(t.address).toLowerCase();
        return name.includes(q) || addr.includes(q);
      });
    }

    // Sortierung
    if (sortBy === 'date') {
      list = tournamentService.sortTournamentsByDate(list, sortAsc);
    } else if (sortBy === 'created') {
      list = list.slice().sort((a, b) => {
        const aC = a.createdAt || 0;
        const bC = b.createdAt || 0;
        return sortAsc ? aC - bC : bC - aC;
      });
    }
    // sortBy === 'quality' -> Backend-Reihenfolge beibehalten

    return list;
  }, [allTournaments, steeldart, edart, dateFilter, customDate, feeRange, searchTerm, sortBy, sortAsc]);

  // Two-Tier Split: mit Bildern zuerst, dann ohne
  const { withImages, withoutImages } = useMemo(
    () => tournamentService.separateTournamentsByImages(filtered),
    [filtered],
  );

  const ordered = useMemo(() => [...withImages, ...withoutImages], [withImages, withoutImages]);

  // Beliebte Turniere (nur wenn keine Suche aktiv)
  const sponsored = useMemo(() => {
    if (searchTerm.trim().length > 0) return [];
    return tournamentService
      .getSponsoredTournaments(allTournaments)
      .slice(0, 6);
  }, [allTournaments, searchTerm]);

  // Reset batch bei Filter-Änderung
  useEffect(() => {
    setVisibleCount(BATCH_SIZE);
  }, [searchTerm, steeldart, edart, dateFilter, customDate, feeRange, sortBy, sortAsc]);

  const hasActiveFilters =
    !steeldart ||
    !edart ||
    dateFilter !== 'all' ||
    feeRange[0] !== 0 ||
    feeRange[1] !== 100 ||
    sortBy !== 'date' ||
    !sortAsc;

  const resetFilters = () => {
    setSteeldart(true);
    setEdart(true);
    setDateFilter('all');
    setCustomDate('');
    setFeeRange([0, 100]);
    setSortBy('date');
    setSortAsc(true);
  };

  // --------------------------------------------
  // Render
  // --------------------------------------------
  return (
    <>
      <Helmet>
        <title>Dart-Turniere - Alle aktuellen Turniere | DartCloud</title>
        <meta
          name="description"
          content={`Entdecke ${allTournaments.length} Dart-Turniere in Deutschland. Steeldart & E-Dart, mit Filter nach Datum, Startgeld und Kategorie.`}
        />
        <meta name="keywords" content="dart turniere, steeldart, edart, turnier finden, dart events" />
      </Helmet>

      <div className="min-h-screen bg-zinc-950">
        {/* Hero */}
        <section className="relative overflow-hidden border-b border-zinc-800">
          {/* Glow-Blobs */}
          <div
            className="pointer-events-none absolute -left-28 -top-28 h-80 w-80 rounded-full blur-3xl"
            style={{ backgroundColor: 'rgba(63,206,209,0.12)' }}
          />
          <div
            className="pointer-events-none absolute -right-20 top-4 h-64 w-64 rounded-full blur-3xl"
            style={{ backgroundColor: 'rgba(63,206,209,0.07)' }}
          />
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(70% 120% at 20% 0%, rgba(63,206,209,0.08), transparent 60%)',
            }}
          />
          <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <span
              className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-wide"
              style={{
                borderColor: 'rgba(63,206,209,0.35)',
                backgroundColor: 'rgba(63,206,209,0.10)',
                color: ACCENT,
              }}
            >
              <Trophy size={13} />
              {loading ? 'Wird geladen …' : `${allTournaments.length} Turniere`}
            </span>
            <h1 className="mt-4 text-4xl md:text-5xl font-extrabold tracking-tight">
              <span className="hero-gradient-text">Dart-Turniere</span>
            </h1>
            <p className="mt-3 max-w-2xl text-base md:text-lg text-gray-400">
              Dein nächstes Match wartet: Steeldart & E-Dart-Turniere in Deutschland &
              Umgebung – filtere nach Datum, Startgeld und Kategorie.
            </p>
          </div>
        </section>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
          {/* Mobile Search + Filter Button */}
          <div className="flex gap-2 mb-4 lg:hidden">
            <Input
              placeholder="Turnier oder Ort suchen..."
              value={searchTerm}
              onValueChange={setSearchTerm}
              startContent={<Search size={18} className="text-gray-400" />}
              classNames={{
                input: 'bg-zinc-900 text-white',
                inputWrapper: 'bg-zinc-900 border border-zinc-800',
              }}
              className="flex-1"
            />
            <Button
              isIconOnly
              variant="flat"
              className="bg-zinc-900 border border-zinc-800"
              onPress={() => setFilterOpen(true)}
              aria-label="Filter öffnen"
            >
              <SlidersHorizontal size={18} className="text-white" />
            </Button>
          </div>

          {/* Active filter chips (Mobile) */}
          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2 mb-4 lg:hidden">
              {!steeldart && (
                <Chip
                  size="sm"
                  variant="flat"
                  onClose={() => setSteeldart(true)}
                  className="bg-zinc-900 text-gray-300"
                >
                  Steeldart aus
                </Chip>
              )}
              {!edart && (
                <Chip
                  size="sm"
                  variant="flat"
                  onClose={() => setEdart(true)}
                  className="bg-zinc-900 text-gray-300"
                >
                  E-Dart aus
                </Chip>
              )}
              {dateFilter !== 'all' && (
                <Chip
                  size="sm"
                  variant="flat"
                  onClose={() => setDateFilter('all')}
                  className="bg-zinc-900 text-gray-300"
                >
                  {dateFilter === 'today'
                    ? 'Heute'
                    : dateFilter === 'week'
                    ? 'Diese Woche'
                    : dateFilter === 'month'
                    ? 'Diesen Monat'
                    : 'Bestimmtes Datum'}
                </Chip>
              )}
              {(feeRange[0] !== 0 || feeRange[1] !== 100) && (
                <Chip
                  size="sm"
                  variant="flat"
                  onClose={() => setFeeRange([0, 100])}
                  className="bg-zinc-900 text-gray-300"
                >
                  {feeRange[0]}-{feeRange[1]}€
                </Chip>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 lg:gap-8">
            {/* Tournament list */}
            <div className="min-w-0 space-y-6">
              {loading ? (
                <SkeletonList />
              ) : error ? (
                <EmptyState
                  title="Fehler beim Laden"
                  text={error}
                  actionLabel="Erneut versuchen"
                  onAction={loadTournaments}
                />
              ) : (
                <>
                  {/* Beliebte Turniere (Premium-Slider) */}
                  {sponsored.length > 0 && (
                    <section>
                      <SectionHeader
                        icon={<Trophy size={18} color={ACCENT} />}
                        title="Beliebte Turniere"
                        subtitle={`${sponsored.length} gesponserte Turniere`}
                      />
                      <div className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-3 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        {sponsored.map((t) => (
                          <SponsoredTournamentCard key={`sp-${t._id}`} tournament={t} />
                        ))}
                      </div>
                    </section>
                  )}

                  {/* Alle Turniere */}
                  <section>
                    <SectionHeader
                      icon={<Calendar size={18} color={ACCENT} />}
                      title="Alle Turniere"
                      subtitle={`${ordered.length} ${ordered.length === 1 ? 'Turnier' : 'Turniere'} gefunden`}
                    />

                    {ordered.length === 0 ? (
                      <EmptyState
                        title="Keine Turniere gefunden"
                        text="Versuche andere Suchbegriffe oder setze die Filter zurück."
                        actionLabel="Filter zurücksetzen"
                        onAction={() => {
                          setSearchTerm('');
                          resetFilters();
                        }}
                      />
                    ) : (
                      <>
                        <div className="space-y-3">
                          {ordered.slice(0, visibleCount).map((t) => (
                            <TournamentCard key={t._id || t.id || t.slug} tournament={t} />
                          ))}
                        </div>
                        {visibleCount < ordered.length && (
                          <div className="flex justify-center mt-6">
                            <Button
                              variant="flat"
                              className="bg-zinc-900 text-white border border-zinc-800"
                              onPress={() => setVisibleCount((c) => c + BATCH_SIZE)}
                            >
                              Mehr laden ({ordered.length - visibleCount} verbleibend)
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </section>
                </>
              )}
            </div>

            {/* Desktop Filter Sidebar */}
            <aside className="hidden lg:block">
              <div className="sticky top-20 space-y-4">
                <Input
                  placeholder="Turnier oder Ort suchen..."
                  value={searchTerm}
                  onValueChange={setSearchTerm}
                  startContent={<Search size={18} className="text-gray-400" />}
                  classNames={{
                    input: 'bg-zinc-900 text-white',
                    inputWrapper: 'bg-zinc-900 border border-zinc-800',
                  }}
                />
                <FilterPanel
                  steeldart={steeldart}
                  onSteeldartChange={setSteeldart}
                  edart={edart}
                  onEdartChange={setEdart}
                  dateFilter={dateFilter}
                  onDateFilterChange={setDateFilter}
                  customDate={customDate}
                  onCustomDateChange={setCustomDate}
                  feeRange={feeRange}
                  onFeeRangeChange={setFeeRange}
                  sortBy={sortBy}
                  onSortByChange={setSortBy}
                  sortAsc={sortAsc}
                  onSortAscChange={setSortAsc}
                  onReset={resetFilters}
                  hasActiveFilters={hasActiveFilters}
                />
              </div>
            </aside>
          </div>
        </div>

        {/* Mobile Filter Modal */}
        <Modal
          isOpen={filterOpen}
          onClose={() => setFilterOpen(false)}
          placement="bottom"
          scrollBehavior="inside"
          size="full"
          classNames={{
            base: 'bg-zinc-950',
            header: 'border-b border-zinc-800',
            footer: 'border-t border-zinc-800',
          }}
        >
          <ModalContent>
            <ModalHeader className="flex items-center gap-2 text-white">
              <SlidersHorizontal size={20} color={ACCENT} />
              Filter & Sortierung
            </ModalHeader>
            <ModalBody className="py-4">
              <FilterPanel
                steeldart={steeldart}
                onSteeldartChange={setSteeldart}
                edart={edart}
                onEdartChange={setEdart}
                dateFilter={dateFilter}
                onDateFilterChange={setDateFilter}
                customDate={customDate}
                onCustomDateChange={setCustomDate}
                feeRange={feeRange}
                onFeeRangeChange={setFeeRange}
                sortBy={sortBy}
                onSortByChange={setSortBy}
                sortAsc={sortAsc}
                onSortAscChange={setSortAsc}
                onReset={resetFilters}
                hasActiveFilters={hasActiveFilters}
                embedded
              />
            </ModalBody>
            <ModalFooter>
              <Button
                variant="light"
                className="text-gray-300"
                onPress={resetFilters}
              >
                Zurücksetzen
              </Button>
              <Button
                className="text-black font-medium"
                style={{ backgroundColor: ACCENT }}
                onPress={() => setFilterOpen(false)}
              >
                Anwenden
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </div>
    </>
  );
};

// ============================================
// Sub-Components
// ============================================

const SectionHeader: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}> = ({ icon, title, subtitle }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className="p-2 rounded-lg" style={{ backgroundColor: `${ACCENT}1A` }}>
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <h2 className="text-lg md:text-xl font-bold text-white truncate">{title}</h2>
      <p className="text-xs md:text-sm text-gray-500 truncate">{subtitle}</p>
    </div>
  </div>
);

const TournamentCard: React.FC<{ tournament: Tournament }> = ({ tournament }) => {
  const navigate = useNavigate();
  const slug = tournament.slug;
  const handleOpen = () => {
    if (slug) navigate(`/turnier/${slug}`);
  };
  const nextTs = tournamentService.getNextTimestamp(tournament);
  const dayCount = tournamentService.getDayCount(tournament);
  const thumb = tournamentService.getThumbnailUrl(tournament, 200);
  const fee = tournamentService.formatEntryFee(tournament.entryFee);
  const addr = tournamentService.formatAddress(tournament.address);
  const timeStr = tournamentService.formatTime(tournament.start);

  const wd = tournamentService.getWeekdayShort(nextTs);
  const dayNum = tournamentService.getDayNumber(nextTs);
  const month = tournamentService.getMonthShort(nextTs);

  const cats = Array.isArray(tournament.categories) ? tournament.categories : [];
  const isSponsored = !!(tournament.sponsored || tournament.featured);
  const isFree =
    tournament.entryFee === null ||
    tournament.entryFee === undefined ||
    (typeof tournament.entryFee === 'number' && tournament.entryFee <= 0);

  return (
    <button
      type="button"
      onClick={handleOpen}
      disabled={!slug}
      className="w-full text-left group disabled:cursor-default"
      aria-label={`Turnier: ${tournament.name}`}
    >
      <div className="flex items-stretch gap-3 p-3 bg-zinc-900 rounded-xl border border-zinc-800 hover:border-[#3fced1]/40 hover:-translate-y-0.5 hover:shadow-[0_12px_32px_-12px_rgba(63,206,209,0.30)] transition-all duration-300 cursor-pointer">
        {/* Date-Badge */}
        <div
          className="flex-shrink-0 w-[72px] rounded-xl flex flex-col items-center justify-center py-2"
          style={{
            background: `linear-gradient(160deg, ${ACCENT}2E 0%, ${ACCENT}10 100%)`,
            border: `1px solid ${ACCENT}45`,
          }}
        >
          <span className="text-[11px] uppercase tracking-wide font-semibold" style={{ color: ACCENT }}>
            {wd}
          </span>
          <span className="text-2xl font-extrabold text-white leading-none my-0.5">
            {dayNum || '?'}
          </span>
          <span className="text-[11px] uppercase tracking-wide text-gray-400">{month}</span>
          {timeStr && (
            <span
              className="mt-1 text-[10px] font-semibold px-1.5 py-0.5 rounded"
              style={{ backgroundColor: ACCENT, color: '#000' }}
            >
              {timeStr}
            </span>
          )}
          {dayCount > 1 && (
            <span className="mt-1 text-[10px] text-gray-400">+{dayCount - 1}</span>
          )}
        </div>

        {/* Image */}
        {thumb && (
          <div className="flex-shrink-0 w-20 md:w-24 h-auto rounded-lg overflow-hidden bg-zinc-800 hidden sm:block">
            <img
              src={thumb}
              alt={tournament.name || 'Turnier'}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0 flex flex-col gap-1.5">
          <div className="flex flex-wrap gap-1">
            {cats.includes('steeldart') && (
              <CategoryChip label="Steeldart" color="#ef4444" />
            )}
            {cats.includes('edart') && (
              <CategoryChip label="E-Dart" color="#f59e0b" />
            )}
            {cats.includes('online') && (
              <CategoryChip label="Online" color="#3b82f6" />
            )}
            {isFree && <CategoryChip label="Kostenlos" color="#22c55e" />}
            {isSponsored && <CategoryChip label="Gesponsert" color="#eab308" />}
          </div>

          <h3 className="text-white font-bold text-base md:text-[17px] leading-tight line-clamp-2 group-hover:text-white">
            {tournament.name || 'Unbenanntes Turnier'}
          </h3>

          <div className="flex items-start gap-1 text-xs md:text-sm text-gray-400">
            <MapPin size={14} className="mt-0.5 flex-shrink-0" />
            <span className="line-clamp-1">{addr}</span>
          </div>

          <div className="flex items-center gap-3 text-xs md:text-sm text-gray-500 mt-auto pt-1">
            <span className="inline-flex items-center gap-1">
              <Clock size={12} />
              {timeStr || '—'}
            </span>
            <span
              className="font-semibold"
              style={{ color: isFree ? '#22c55e' : ACCENT }}
            >
              {fee}
            </span>
          </div>
        </div>

        {/* Chevron */}
        <div className="flex items-center flex-shrink-0 text-gray-500 group-hover:text-white transition-colors">
          <ChevronRight size={20} />
        </div>
      </div>
    </button>
  );
};

/** Premium-Karte für gesponserte Turniere (horizontaler Slider) */
const SponsoredTournamentCard: React.FC<{ tournament: Tournament }> = ({ tournament }) => {
  const navigate = useNavigate();
  const slug = tournament.slug;
  const handleOpen = () => {
    if (slug) navigate(`/turnier/${slug}`);
  };
  const nextTs = tournamentService.getNextTimestamp(tournament);
  const thumb = tournamentService.getThumbnailUrl(tournament, 400);
  const fee = tournamentService.formatEntryFee(tournament.entryFee);
  const addr = tournamentService.formatAddress(tournament.address);
  const timeStr = tournamentService.formatTime(tournament.start);
  const wd = tournamentService.getWeekdayShort(nextTs);
  const dayNum = tournamentService.getDayNumber(nextTs);
  const month = tournamentService.getMonthShort(nextTs);

  return (
    <button
      type="button"
      onClick={handleOpen}
      disabled={!slug}
      aria-label={`Turnier: ${tournament.name || 'Unbenanntes Turnier'}`}
      className="group relative h-52 w-[300px] sm:h-56 sm:w-[340px] shrink-0 snap-start overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 text-left transition-all duration-300 hover:-translate-y-1 hover:border-[#3fced1]/50 hover:shadow-[0_16px_40px_-12px_rgba(63,206,209,0.35)] disabled:cursor-default"
    >
      {/* Hintergrund */}
      <div className="absolute inset-0">
        {thumb ? (
          <img
            src={thumb}
            alt={tournament.name || 'Turnier'}
            loading="lazy"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div
            className="flex h-full w-full items-center justify-center bg-gradient-to-br from-zinc-800 via-zinc-900 to-black"
            style={{
              backgroundImage:
                'radial-gradient(60% 80% at 30% 20%, rgba(63,206,209,0.10), transparent 65%)',
            }}
          >
            <Trophy size={44} className="text-zinc-600" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/35 to-transparent" />
      </div>

      {/* Gesponsert-Badge */}
      <span
        className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-bold text-black shadow-lg"
        style={{ backgroundColor: ACCENT }}
      >
        <Trophy size={12} /> Gesponsert
      </span>

      {/* Date-Badge */}
      <div
        className="absolute right-3 top-3 flex w-[54px] flex-col items-center rounded-xl py-1.5 backdrop-blur-sm"
        style={{ backgroundColor: 'rgba(0,0,0,0.55)', border: `1px solid ${ACCENT}45` }}
      >
        <span className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: ACCENT }}>
          {wd}
        </span>
        <span className="text-lg font-extrabold leading-none text-white">{dayNum || '?'}</span>
        <span className="text-[10px] uppercase tracking-wide text-gray-300">{month}</span>
      </div>

      {/* Inhalt unten */}
      <div className="absolute inset-x-0 bottom-0 p-4">
        <h3 className="line-clamp-2 text-lg font-bold leading-tight text-white">
          {tournament.name || 'Unbenanntes Turnier'}
        </h3>
        <div className="mt-1 flex items-center gap-1 text-sm text-gray-300">
          <MapPin size={13} className="shrink-0" style={{ color: ACCENT }} />
          <span className="line-clamp-1">{addr}</span>
        </div>
        <div className="mt-1.5 flex items-center gap-3 text-xs text-gray-400">
          {timeStr && (
            <span className="inline-flex items-center gap-1">
              <Clock size={11} />
              {timeStr}
            </span>
          )}
          <span className="font-semibold" style={{ color: ACCENT }}>
            {fee}
          </span>
        </div>
      </div>
    </button>
  );
};

const CategoryChip: React.FC<{ label: string; color: string }> = ({ label, color }) => (
  <span
    className="inline-flex items-center text-[10px] md:text-[11px] font-semibold px-1.5 py-0.5 rounded-md"
    style={{ backgroundColor: `${color}22`, color }}
  >
    {label}
  </span>
);

const SkeletonList: React.FC = () => (
  <div className="space-y-3">
    {Array.from({ length: 5 }).map((_, i) => (
      <div
        key={i}
        className="h-[110px] bg-zinc-900 border border-zinc-800 rounded-xl animate-pulse"
      />
    ))}
    <div className="flex justify-center pt-4">
      <Spinner color="primary" size="sm" />
    </div>
  </div>
);

const EmptyState: React.FC<{
  title: string;
  text: string;
  actionLabel?: string;
  onAction?: () => void;
}> = ({ title, text, actionLabel, onAction }) => (
  <div className="text-center py-12 md:py-16 bg-zinc-900 border border-zinc-800 rounded-xl">
    <Trophy size={48} className="mx-auto text-gray-600 mb-4" />
    <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
    <p className="text-gray-400 mb-4">{text}</p>
    {actionLabel && onAction && (
      <Button
        variant="flat"
        className="bg-zinc-800 text-white"
        startContent={<X size={16} />}
        onPress={onAction}
      >
        {actionLabel}
      </Button>
    )}
  </div>
);

// --------------------------------------------
// Filter Panel
// --------------------------------------------
interface FilterPanelProps {
  steeldart: boolean;
  onSteeldartChange: (v: boolean) => void;
  edart: boolean;
  onEdartChange: (v: boolean) => void;
  dateFilter: DateFilter;
  onDateFilterChange: (v: DateFilter) => void;
  customDate: string;
  onCustomDateChange: (v: string) => void;
  feeRange: [number, number];
  onFeeRangeChange: (v: [number, number]) => void;
  sortBy: SortBy;
  onSortByChange: (v: SortBy) => void;
  sortAsc: boolean;
  onSortAscChange: (v: boolean) => void;
  onReset: () => void;
  hasActiveFilters: boolean;
  embedded?: boolean;
}

const FilterPanel: React.FC<FilterPanelProps> = (props) => {
  const {
    steeldart,
    onSteeldartChange,
    edart,
    onEdartChange,
    dateFilter,
    onDateFilterChange,
    customDate,
    onCustomDateChange,
    feeRange,
    onFeeRangeChange,
    sortBy,
    onSortByChange,
    sortAsc,
    onSortAscChange,
    onReset,
    hasActiveFilters,
    embedded = false,
  } = props;

  const categoriesActive = !steeldart || !edart;
  const dateActive = dateFilter !== 'all';
  const feeActive = feeRange[0] !== 0 || feeRange[1] !== 100;
  const sortActive = sortBy !== 'date' || !sortAsc;

  return (
    <div
      className={
        embedded
          ? 'divide-y divide-zinc-800'
          : 'divide-y divide-zinc-800 bg-zinc-900 border border-zinc-800 rounded-xl p-4'
      }
    >
      {/* Kategorien */}
      <div className="pb-5">
        <FilterSectionTitle label="Kategorien" active={categoriesActive} />
        <div className="flex flex-col gap-1">
          <Checkbox
            isSelected={steeldart}
            onValueChange={onSteeldartChange}
            classNames={{ label: 'text-white text-sm' }}
          >
            <span className="text-red-500 font-medium">Steeldart</span>
          </Checkbox>
          <Checkbox
            isSelected={edart}
            onValueChange={onEdartChange}
            classNames={{ label: 'text-white text-sm' }}
          >
            <span className="text-orange-500 font-medium">E-Dart</span>
          </Checkbox>
        </div>
      </div>

      {/* Zeitraum */}
      <div className="py-5">
        <FilterSectionTitle label="Zeitraum" active={dateActive} />
        <RadioGroup
          value={dateFilter}
          onValueChange={(v) => onDateFilterChange(v as DateFilter)}
          classNames={{ wrapper: 'gap-1' }}
        >
          <Radio value="all" classNames={{ label: 'text-white text-sm' }}>
            Alle
          </Radio>
          <Radio value="today" classNames={{ label: 'text-white text-sm' }}>
            Heute
          </Radio>
          <Radio value="week" classNames={{ label: 'text-white text-sm' }}>
            Diese Woche
          </Radio>
          <Radio value="month" classNames={{ label: 'text-white text-sm' }}>
            Diesen Monat
          </Radio>
          <Radio value="custom" classNames={{ label: 'text-white text-sm' }}>
            Bestimmtes Datum
          </Radio>
        </RadioGroup>
        {dateFilter === 'custom' && (
          <input
            type="date"
            value={customDate}
            onChange={(e) => onCustomDateChange(e.target.value)}
            className="mt-2 w-full bg-zinc-800 border border-zinc-700 text-white rounded-md px-2 py-1.5 text-sm focus:outline-none focus:border-zinc-500"
          />
        )}
      </div>

      {/* Startgeld */}
      <div className="py-5">
        <FilterSectionTitle label="Startgeld" active={feeActive} />
        <div className="flex flex-wrap gap-1.5 mb-3">
          <QuickFeeButton
            label="Alle"
            active={feeRange[0] === 0 && feeRange[1] === 100}
            onClick={() => onFeeRangeChange([0, 100])}
          />
          <QuickFeeButton
            label="Kostenlos"
            active={feeRange[0] === 0 && feeRange[1] === 0}
            onClick={() => onFeeRangeChange([0, 0])}
          />
          <QuickFeeButton
            label="Bis 10€"
            active={feeRange[0] === 0 && feeRange[1] === 10}
            onClick={() => onFeeRangeChange([0, 10])}
          />
          <QuickFeeButton
            label="Bis 25€"
            active={feeRange[0] === 0 && feeRange[1] === 25}
            onClick={() => onFeeRangeChange([0, 25])}
          />
        </div>
        <Slider
          label={`${feeRange[0]} € – ${feeRange[1]} €`}
          size="sm"
          step={1}
          minValue={0}
          maxValue={100}
          value={feeRange}
          onChange={(v) =>
            Array.isArray(v) ? onFeeRangeChange([v[0], v[1]]) : onFeeRangeChange([0, v])
          }
          classNames={{
            label: 'text-gray-400 text-xs',
            value: 'text-white text-xs',
            track: 'bg-zinc-800',
            filler: `bg-[${ACCENT}]`,
          }}
        />
      </div>

      {/* Sortierung */}
      <div className="py-5">
        <FilterSectionTitle label="Sortierung" active={sortActive} />
        <Select
          size="sm"
          selectedKeys={[sortBy]}
          onChange={(e) => onSortByChange(e.target.value as SortBy)}
          classNames={{
            trigger: 'bg-zinc-800 border border-zinc-700',
            value: 'text-white text-sm',
            popoverContent: 'bg-zinc-900',
          }}
          aria-label="Sortierung"
        >
          <SelectItem key="quality" className="text-white">Standard</SelectItem>
          <SelectItem key="date" className="text-white">Turnierdatum</SelectItem>
          <SelectItem key="created" className="text-white">Erstellungsdatum</SelectItem>
        </Select>
        {sortBy !== 'quality' && (
          <Button
            size="sm"
            variant="flat"
            className="mt-2 bg-zinc-800 text-white w-full"
            startContent={<ArrowUpDown size={14} />}
            onPress={() => onSortAscChange(!sortAsc)}
          >
            {sortAsc ? 'Aufsteigend' : 'Absteigend'}
          </Button>
        )}
      </div>

      {/* Reset */}
      {hasActiveFilters && !embedded && (
        <div className="pt-5">
          <Button
            size="sm"
            variant="flat"
            className="w-full bg-zinc-800 text-white"
            startContent={<X size={14} />}
            onPress={onReset}
          >
            Filter zurücksetzen
          </Button>
        </div>
      )}
    </div>
  );
};

/** Sektions-Überschrift im Filter-Panel – mit Akzent-Punkt bei aktivem Filter */
const FilterSectionTitle: React.FC<{ label: string; active: boolean }> = ({
  label,
  active,
}) => (
  <h4
    className={`text-xs font-bold uppercase tracking-wide mb-2 flex items-center gap-1.5 ${
      active ? '' : 'text-gray-500'
    }`}
    style={active ? { color: ACCENT } : undefined}
  >
    {label}
    {active && (
      <span
        className="inline-block h-1.5 w-1.5 rounded-full"
        style={{ backgroundColor: ACCENT }}
      />
    )}
  </h4>
);

const QuickFeeButton: React.FC<{
  label: string;
  active: boolean;
  onClick: () => void;
}> = ({ label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={
      'px-2 py-1 rounded-md text-xs font-medium transition-colors ' +
      (active
        ? 'text-black'
        : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700')
    }
    style={active ? { backgroundColor: ACCENT } : undefined}
  >
    {label}
  </button>
);

export default TournamentPage;
